import * as Schema from "effect/Schema";
import { OperatorProjectId } from "./baseSchemas.ts";
import { OperatorVaultSecretName } from "./operator.ts";

const Id = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200));
export const ManagedGitHost = Schema.Literals(["github", "gitlab", "bitbucket"]);
export type ManagedGitHost = typeof ManagedGitHost.Type;
export const ManagedRepository = Schema.Struct({
  id: Id,
  name: Schema.String,
  url: Schema.String,
  defaultBranch: Schema.String,
  canWrite: Schema.Boolean,
  isPrivate: Schema.optionalKey(Schema.Boolean),
  canEditWorkflows: Schema.optionalKey(Schema.Boolean),
  installationId: Schema.optionalKey(Schema.String),
});
export type ManagedRepository = typeof ManagedRepository.Type;
export const ManagedSourceControlConnection = Schema.Struct({
  id: Id,
  host: ManagedGitHost,
  accountId: Schema.optionalKey(Schema.String),
  accountName: Schema.optionalKey(Schema.String),
  status: Schema.Literals([
    "connecting",
    "connected",
    "reconnect_required",
    "revoked",
    "disconnected",
  ]),
  generation: Schema.Int,
  repositories: Schema.Array(ManagedRepository),
  updatedAt: Schema.String,
  error: Schema.optionalKey(Schema.String),
});
export type ManagedSourceControlConnection = typeof ManagedSourceControlConnection.Type;
export const RepositorySettings = Schema.Struct({
  repository: ManagedRepository,
  baseBranch: Schema.optionalKey(Schema.String),
  variables: Schema.Array(Schema.Struct({ name: Schema.String })),
});
export type RepositorySettings = typeof RepositorySettings.Type;
const RepositoryVariableValue = Schema.String.check(
  Schema.isMaxLength(65536),
  Schema.makeFilter(
    (value) => !value.includes("\0") || "Environment values cannot contain null bytes.",
  ),
);
export const SourceControlConnectionAction = Schema.Union([
  Schema.Struct({ action: Schema.Literal("list") }),
  Schema.Struct({
    action: Schema.Literal("repository-settings"),
    connectionId: Id,
    repositoryId: Id,
  }),
  Schema.Struct({
    action: Schema.Literal("repository-branches"),
    connectionId: Id,
    repositoryId: Id,
    cursor: Schema.optionalKey(Schema.String),
  }),
  Schema.Struct({
    action: Schema.Literal("update-repository"),
    connectionId: Id,
    repositoryId: Id,
    baseBranch: Schema.NullOr(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(250))),
  }),
  Schema.Struct({
    action: Schema.Literal("set-repository-variable"),
    connectionId: Id,
    repositoryId: Id,
    name: OperatorVaultSecretName,
    value: RepositoryVariableValue,
  }),
  Schema.Struct({
    action: Schema.Literal("set-repository-variables"),
    connectionId: Id,
    repositoryId: Id,
    variables: Schema.Array(
      Schema.Struct({ name: OperatorVaultSecretName, value: RepositoryVariableValue }),
    ).check(Schema.isMinLength(1), Schema.isMaxLength(100)),
  }),
  Schema.Struct({
    action: Schema.Literal("delete-repository-variable"),
    connectionId: Id,
    repositoryId: Id,
    name: OperatorVaultSecretName,
  }),
  Schema.Struct({
    action: Schema.Literal("reveal-repository-variable"),
    connectionId: Id,
    repositoryId: Id,
    name: OperatorVaultSecretName,
  }),
  Schema.Struct({
    action: Schema.Literal("start"),
    host: ManagedGitHost,
    accountToken: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(16000)),
    connectionId: Schema.optionalKey(Id),
  }),
  Schema.Struct({ action: Schema.Literal("complete"), connectionId: Id }),
  Schema.Struct({
    action: Schema.Literal("repositories"),
    connectionId: Id,
    cursor: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(2000))),
  }),
  Schema.Struct({
    action: Schema.Literal("grant"),
    connectionId: Id,
    repositoryIds: Schema.Array(Id).check(Schema.isMaxLength(100)),
  }),
  Schema.Struct({
    action: Schema.Literal("bind"),
    connectionId: Id,
    repositoryId: Id,
    projectId: OperatorProjectId,
  }),
  Schema.Struct({ action: Schema.Literals(["disconnect", "cancel"]), connectionId: Id }),
]);
export type SourceControlConnectionAction = typeof SourceControlConnectionAction.Type;
export const SourceControlConnectionResult = Schema.Struct({
  connections: Schema.Array(ManagedSourceControlConnection),
  hosts: Schema.Array(ManagedGitHost),
  authorizationUrl: Schema.optionalKey(Schema.String),
  installationUrl: Schema.optionalKey(Schema.String),
  repositories: Schema.optionalKey(Schema.Array(ManagedRepository)),
  nextCursor: Schema.optionalKey(Schema.String),
  boundRepository: Schema.optionalKey(ManagedRepository),
  repositorySettings: Schema.optionalKey(RepositorySettings),
  repositoryVariable: Schema.optionalKey(
    Schema.Struct({
      name: OperatorVaultSecretName,
      value: RepositoryVariableValue,
    }),
  ),
  branches: Schema.optionalKey(Schema.Array(Schema.String)),
});
export type SourceControlConnectionResult = typeof SourceControlConnectionResult.Type;
